import type { UserExtension as UserModel } from 'tio-data/data/resources/user.ext';

/**
 * Identity + role payload the TAS API needs on every state-transition
 * to attribute the action. Both apps build this same shape — they just
 * source `actingWithRole` from different places:
 *
 * - **admin**: heuristic on `user.isTasTioApprover`. If true, the user
 *   is acting via their EMPLOYEE delegation as `TAS.Approver.TIO_APPROVER`;
 *   otherwise as a plain TIO_ADMIN. The endpoints reject the call if
 *   the role isn't valid, so this heuristic is safe-ish but is the
 *   first thing to look at when a transition is rejected unexpectedly.
 * - **employee**: selects the TAS-capable role the user holds at the company
 *   they are acting within. Attribution follows the action rather than a
 *   session-wide toggle, so holding a non-TAS role too cannot get the
 *   transition rejected.
 *
 * tio-common components that don't know which app they're rendered in
 * call `buildTasActorFromSession(this.sessionContext)` and let the
 * dispatcher pick based on whether `currentRole` is set (employee
 * only). Per-app callers that already know their context use
 * `buildTasAdminActor(user)` or `buildTasEmployeeActor(user, currentRole)`
 * directly.
 */
export interface TasActor {
  name: string;
  userId: string;
  actingWithRole: { role: string; relationshipType: string };
}

/** @deprecated Prefer {@link TasActor}; alias kept for incremental renames. */
export type TasAdminActor = TasActor;

export function buildTasAdminActor(user: UserModel): TasActor {
  const isTasTioApprover = user.isTasTioApprover;
  return {
    name: user.person?.fullName ?? '',
    userId: (user.id as string) ?? '',
    actingWithRole: {
      role: isTasTioApprover ? 'EMPLOYEE' : 'TIO_ADMIN',
      relationshipType: isTasTioApprover ? 'TAS.Approver.TIO_APPROVER' : 'TIO_ADMIN',
    },
  };
}

/**
 * Minimum shape buildTasEmployeeActor needs from a Role record. Stays
 * structural so the helper doesn't have to import the per-app Role
 * type (employee's session-context types `currentRole` from its own
 * role model).
 */
export interface TasEmployeeRole {
  role?: string;
  relationshipType?: string;
}

/**
 * TAS-capable relationship types, least privileged first.
 *
 * `actingWithRole` is the audit record of the capacity an action was taken in, so it
 * names the narrowest role permitting the action rather than the strongest one the
 * person happens to hold. Selection walks this order and stops at the first match;
 * picking by array position would attribute to whichever role the API listed first,
 * which is arbitrary.
 *
 * PARTICIPANT leads and is excluded when approving — a participant can submit,
 * withdraw and supply evidence on their own application, but cannot approve one.
 */
const TAS_ROLE_PRECEDENCE: ReadonlyArray<(relationshipType: string) => boolean> = [
  (t) => t === 'PARTICIPANT',
  (t) => t.startsWith('TAS.Approver'),
  (t) => t.startsWith('TAS.Admin'),
  (t) => t === 'EMPLOYEE_ADMIN',
  (t) => t === 'ACCOUNT_OWNER',
  (t) => t === 'TIO_ACCOUNT_MANAGER',
  (t) => t === 'TIO_ADMIN',
];

/** Index of the first tier that may approve; PARTICIPANT may not. */
const FIRST_APPROVING_TIER = 1;

function leastPrivileged(
  candidates: ReadonlyArray<TasEmployeeRole | undefined>,
  fromTier: number
): TasEmployeeRole | undefined {
  for (const tier of TAS_ROLE_PRECEDENCE.slice(fromTier)) {
    const match = candidates.find(
      (role) => !!role?.relationshipType && tier(role.relationshipType)
    );
    if (match) return match;
  }
  return undefined;
}

function actor(user: UserModel, role: TasEmployeeRole | undefined): TasActor {
  return {
    name: user.person?.fullName ?? '',
    userId: (user.id as string) ?? '',
    actingWithRole: {
      role: role?.role ?? '',
      relationshipType: role?.relationshipType ?? '',
    },
  };
}

export function buildTasEmployeeActor(
  user: UserModel,
  currentRole: TasEmployeeRole | TasEmployeeRole[] | undefined
): TasActor {
  const candidates = Array.isArray(currentRole) ? currentRole : [currentRole];
  return actor(user, leastPrivileged(candidates, 0) ?? candidates[0]);
}

/**
 * Actor for an approval decision, where PARTICIPANT is not a capacity that permits
 * the action — selection starts at the approver tier.
 *
 * @param user the acting user
 * @param roles every role held at the company being acted within
 */
export function buildTasApproverActor(
  user: UserModel,
  roles: TasEmployeeRole | TasEmployeeRole[] | undefined
): TasActor {
  const candidates = Array.isArray(roles) ? roles : [roles];
  return actor(user, leastPrivileged(candidates, FIRST_APPROVING_TIER) ?? candidates[0]);
}

/**
 * tio-common dispatcher: returns the right actor shape from whatever
 * sessionContext is available at runtime. If `currentRole` is set
 * (tio-employee), uses the employee builder; otherwise (tio-admin)
 * applies the isTasTioApprover heuristic.
 */
export interface TasActorSessionShape {
  user: UserModel;
  currentRole?: TasEmployeeRole;
  rolesForCurrentCompany?: TasEmployeeRole[];
}

export function buildTasActorFromSession(session: TasActorSessionShape): TasActor {
  if (session.currentRole) {
    // Every caller of this dispatcher is an approval surface.
    return buildTasApproverActor(
      session.user,
      session.rolesForCurrentCompany ?? session.currentRole
    );
  }
  return buildTasAdminActor(session.user);
}
